import React from "react"

import { Avatar } from "@coval/ui"

import type { TranscriptParticipantLabels } from "@/components/transcript/transcriptParticipants"

import {
  AGENT_CHANNEL_INDEX,
  PERSONA_CHANNEL_INDEX,
} from "./useAudioWaveformOptions"

/** The 20px avatar plus the gap between it and the well. */
export const SPEAKER_GUTTER_PIXELS = 32

/** What the two bands are held apart by, so they read as one surface each. */
const LANE_GAP_PIXELS = 2

const LANE_BANDS = [
  { channel: AGENT_CHANNEL_INDEX, className: "bg-coval-chat-agent-surface" },
  { channel: PERSONA_CHANNEL_INDEX, className: "bg-coval-media-track" },
]

/**
 * A band per speaker behind the canvases, each carrying that speaker's chat
 * bubble surface. Each band gives up half the gap on the side facing the split,
 * so the card behind the well shows through as a hairline between them — which
 * is why the well itself draws no background while these are up.
 */
export const SpeakerLaneBands: React.FC<{ laneHeight: number }> = ({
  laneHeight,
}) => (
  <>
    {LANE_BANDS.map(({ channel, className }) => (
      <div
        key={channel}
        aria-hidden
        className={`absolute inset-x-0 ${className}`}
        style={{
          height: laneHeight - LANE_GAP_PIXELS / 2,
          top:
            channel * laneHeight +
            (channel > AGENT_CHANNEL_INDEX ? LANE_GAP_PIXELS / 2 : 0),
        }}
      />
    ))}
  </>
)

/**
 * Faces the two lanes from the gutter to the left of the well, in the channel
 * order the waveform draws them. Decorative: the transcript beside the player
 * already names both speakers.
 */
export const SpeakerLaneAvatars: React.FC<{
  laneHeight: number
  participants: TranscriptParticipantLabels
}> = ({ laneHeight, participants }) => {
  const lanes = [
    {
      channel: AGENT_CHANNEL_INDEX,
      name: participants.agentLabel,
      src: participants.agentAvatarUrl,
      tint: participants.agentAvatarTint,
    },
    {
      channel: PERSONA_CHANNEL_INDEX,
      name: participants.userLabel,
      src: participants.userAvatarUrl,
      tint: participants.userAvatarTint,
    },
  ]

  return (
    <div
      className="flex shrink-0 flex-col"
      style={{ width: SPEAKER_GUTTER_PIXELS }}
    >
      {lanes.map((lane) => (
        <div
          key={lane.channel}
          className="flex items-center"
          style={{ height: laneHeight }}
        >
          <Avatar
            size="sm"
            name={lane.name}
            src={lane.src}
            tint={lane.tint}
            decorative
          />
        </div>
      ))}
    </div>
  )
}
